// frontend/src/components/FaceVerification.jsx
import React, { useState, useRef, useEffect } from 'react';
import { CameraIcon, CheckIcon, XIcon } from '@heroicons/react/outline';
import api from '../services/api';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';

const FaceVerification = ({
    accessToken,
    onVerificationComplete,
    onTestPaused,
    onCameraOff,
    onFaceVerified
}) => {
    const { language } = useLanguage();
    const t = (key, replacements) => getTranslation(language, key, replacements);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [stream, setStream] = useState(null);
    const [cameraReady, setCameraReady] = useState(false);
    const [error, setError] = useState(null);
    const [lastVerified, setLastVerified] = useState(null);
    const [verifiedRecently, setVerifiedRecently] = useState(true);

    const videoRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        if (isExpanded && !stream) {
            startCamera();
        } else if (!isExpanded && stream) {
            stopCamera();
        }
        return () => {
            stopCamera();
        };
    }, [isExpanded]);

    const startCamera = async () => {
        try {
            setError(null);
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 320 },
                    height: { ideal: 240 }
                },
                audio: false
            });
            streamRef.current = mediaStream;
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                    setCameraReady(true);
                };
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            setError(t('faceverify.camera_error') || 'Unable to access camera');
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setStream(null);
        setCameraReady(false);
    };

    const handleVerify = async () => {
        if (!videoRef.current) return;
        try {
            setIsVerifying(true);

            const video = videoRef.current;
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 320;
            canvas.height = video.videoHeight || 240;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = canvas.toDataURL('image/jpeg', 0.8);

            const response = await api.post(`/face-verification/${accessToken}/verify`, {
                image: imageData
            });

            if (response.data.success) {
                setVerifiedRecently(true);
                setLastVerified(new Date());
                onFaceVerified?.();
                onVerificationComplete?.(true);

                setTimeout(() => {
                    setIsExpanded(false);
                    stopCamera();
                }, 1000);
            } else {
                setError(response.data.error || (t('faceverify.verify_failed') || 'Face verification failed'));
                onVerificationComplete?.(false);
            }
        } catch (err) {
            console.error('Verification error:', err);
            setError(err.response?.data?.error || (t('faceverify.verify_failed') || 'Face verification failed'));
            onVerificationComplete?.(false);
        } finally {
            setIsVerifying(false);
        }
    };

    // ============================================
    // COLLAPSED VIEW — small floating button
    // ============================================
    if (!isExpanded) {
        return (
            <div
                className="fixed z-40 
                    bottom-3 left-1/2 -translate-x-1/2
                    sm:bottom-6 sm:left-auto sm:translate-x-0 sm:right-6"
            >
                <button
                    onClick={() => setIsExpanded(true)}
                    className="
                        bg-white rounded-full sm:rounded-lg 
                        shadow-lg border border-gray-200 
                        p-2 sm:p-2.5
                        hover:shadow-xl transition-shadow
                        flex items-center gap-2
                    "
                    title={t('faceverify.verify_face') || 'Verify Face'}
                >
                    <div className="
                        w-9 h-9 sm:w-10 sm:h-10 
                        bg-[#ff9f2f]/10 rounded-full 
                        flex items-center justify-center
                    ">
                        <CameraIcon className="h-5 w-5 text-[#ff9f2f]" />
                    </div>
                    <span className="
                        hidden sm:inline-block
                        text-xs font-medium text-gray-700
                        pr-1
                    ">
                        {t('faceverify.verify_face') || 'Verify Face'}
                    </span>
                </button>
            </div>
        );
    }

    // ============================================
    // EXPANDED VIEW — full camera widget
    // ============================================
    return (
        <div
            className="fixed z-50 
                bottom-3 left-1/2 -translate-x-1/2 w-[92vw] max-w-sm
                sm:bottom-6 sm:left-auto sm:translate-x-0 sm:right-6 sm:w-72"
        >
            <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="bg-[#063b56] px-3 py-2 flex items-center justify-between">
                    <span className="text-white text-sm font-semibold">
                        {/* {t('faceverify.title') || 'Face Verification'} */}
                    </span>
                    <button
                        onClick={() => {
                            setIsExpanded(false);
                            stopCamera();
                        }}
                        className="text-white hover:text-gray-300"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                {/* Camera Preview */}
                <div className="relative bg-black">
                    <video
                        ref={videoRef}
                        className="w-full h-40 sm:h-40 object-cover"
                        autoPlay
                        playsInline
                        muted
                    />
                    {!cameraReady && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-800/50">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    )}
                    {verifiedRecently && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                            <CheckIcon className="h-3 w-3" />
                        </div>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-red-700 text-xs">
                        {error}
                    </div>
                )}

                {/* Instructions */}
                {/* <div className="px-3 py-2 bg-blue-50 border-b border-blue-200">
                    <p className="text-xs text-blue-600 text-center">
                        {t('faceverify.instruction') || 'Look directly at the camera and click Verify'}
                    </p>
                </div> */}

                {/* Verify Button */}
                {/* <div className="p-3">
                    <button
                        onClick={handleVerify}
                        disabled={!cameraReady || isVerifying}
                        className="w-full py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] 
                            disabled:opacity-50 flex items-center justify-center text-sm font-medium"
                    >
                        {isVerifying ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                {t('faceverify.verifying') || 'Verifying...'}
                            </>
                        ) : (
                            <>
                                <CameraIcon className="h-4 w-4 mr-2" />
                                {t('faceverify.verify_face') || 'Verify Face'}
                            </>
                        )}
                    </button>
                    {lastVerified && (
                        <p className="text-xs text-gray-500 text-center mt-2">
                            {t('faceverify.last_verified') || 'Last verified'}: {lastVerified.toLocaleTimeString()}
                        </p>
                    )}
                </div> */}
            </div>
        </div>
    );
};

export default FaceVerification;